import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { errMessage } from "../lib/errors";
import { Truck, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import {
  deliveryInfo,
  deliveryZones,
  deliveryDefaultFee,
  restaurantInfo
} from "../data/mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function findZone(postcode) {
  const pc = (postcode || "").trim();
  if (!/^\d{4}$/.test(pc)) return null;
  const match = deliveryZones.find((z) => z.postcodes.includes(pc));
  if (match) return { ...match, matched: true };
  // Fallback: default outer fee
  return { ...deliveryDefaultFee, matched: false, range: "30+ km", areas: "Outer South East QLD" };
}

export default function Delivery() {
  const [postcode, setPostcode] = useState("");
  const [zone, setZone] = useState(null);
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [enquired, setEnquired] = useState(false);
  const { total } = useCart();
  const { user, authHeaders } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      setName((n) => n || user.name || "");
      setPhone((p) => p || user.phone || "");
      setAddress((a) => a || user.address || "");
      setPostcode((pc) => pc || user.postcode || "");
    }
     
  }, [user]);

  const meetsMinimum = total >= deliveryInfo.minOrder;

  const check = (e) => {
    e.preventDefault();
    const found = findZone(postcode);
    if (!found) {
      toast({
        title: "Postcode not valid",
        description: "Please enter a 4-digit Australian postcode."
      });
      return;
    }
    setZone(found);
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!name || !phone || !postcode || !address) {
      toast({ title: "Missing details", description: "Please fill in your name, phone, address and postcode." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/delivery-enquiries`,
        {
          name, phone, address, postcode,
          zone: zone?.zone,
          fee: zone?.fee,
          cart_total: total || undefined,
          notes: notes || undefined
        },
        { headers: authHeaders }
      );
      setEnquired(true);
      toast({
        title: "Enquiry received",
        description: "We'll call you shortly to confirm your delivery order."
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: errMessage(err, "Please try again or call us.")
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="delivery" className="py-24 md:py-32 bg-[var(--mm-bg-alt)] border-t border-b border-[var(--mm-line)]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="text-center mb-14 fade-in">
          <div className="section-label mb-4">Home Delivery</div>
          <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95]">
            To your <span className="text-[var(--mm-accent)]">door.</span>
          </h2>
          <p className="text-[var(--mm-cream-muted)] mt-6 max-w-2xl mx-auto font-light leading-relaxed">
            {deliveryInfo.note} Minimum order <span className="text-[var(--mm-accent)]">${deliveryInfo.minOrder} AUD</span>.
          </p>
        </div>

        {/* Postcode checker */}
        <div className="max-w-2xl mx-auto p-6 md:p-8 bg-[var(--mm-bg)] border border-[var(--mm-accent)] mb-14 fade-in">
          <div className="flex items-center gap-3 mb-5">
            <MapPin size={18} className="text-[var(--mm-accent)]" />
            <span className="section-label">Delivery Fee Calculator</span>
          </div>
          <form onSubmit={check} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="Enter your 4-digit postcode"
              className="flex-1 bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none tracking-widest text-lg"
            />
            <button type="submit" className="btn-primary">Check Fee</button>
          </form>

          {zone && (
            <div className="mt-6 pt-6 border-t border-[var(--mm-line)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-1">
                  {zone.zone || "Outer SEQ"} · {zone.range}
                </div>
                <div className="font-display italic text-xl text-[var(--mm-cream)] leading-tight">
                  {zone.areas}
                </div>
                {!zone.matched && (
                  <div className="text-xs text-[var(--mm-cream-muted)] font-light mt-2">
                    Postcode is outside our standard zones — final fee confirmed on enquiry.
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-1">Delivery Fee</div>
                <div className="font-display text-4xl text-[var(--mm-accent)]">${zone.fee}</div>
              </div>
            </div>
          )}
        </div>

        {/* Map embed */}
        <div className="mb-14 fade-in aspect-[16/6] overflow-hidden border border-[var(--mm-line)]">
          <iframe
            title="Mamamina's Soulfeed delivery area"
            src="https://www.google.com/maps?q=1422+Beaudesert+Rd+Acacia+Ridge+QLD+4110&z=11&output=embed"
            className="w-full h-full grayscale"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Zones grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-16 fade-in">
          {deliveryZones.map((z) => (
            <div key={z.zone} className="p-5 border border-[var(--mm-line)] hover:border-[var(--mm-accent)] transition-colors duration-300">
              <div className="flex items-baseline justify-between mb-3">
                <div className="section-label">{z.zone}</div>
                <div className="font-display text-2xl text-[var(--mm-accent)]">${z.fee}</div>
              </div>
              <div className="text-[0.72rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-3">{z.range}</div>
              <p className="text-xs text-[var(--mm-cream-muted)] font-light leading-relaxed">
                {z.areas}
              </p>
            </div>
          ))}
        </div>

        {/* Enquiry form */}
        {!enquired ? (
          <div className="max-w-3xl mx-auto p-8 md:p-10 border border-[var(--mm-line)] bg-[var(--mm-bg)] fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Truck size={20} className="text-[var(--mm-accent)]" />
              <div>
                <div className="section-label">Request Delivery</div>
                <h3 className="font-display italic text-2xl text-[var(--mm-cream)] mt-1">
                  Book a private delivery
                </h3>
              </div>
            </div>

            {total > 0 && (
              <div
                className={`mb-6 p-4 border ${
                  meetsMinimum
                    ? "border-[var(--mm-accent)] text-[var(--mm-cream)]"
                    : "border-[var(--mm-line)] text-[var(--mm-cream-muted)]"
                } text-sm`}
              >
                {meetsMinimum ? (
                  <span>
                    <CheckCircle2 size={14} className="inline mr-2 text-[var(--mm-accent)]" />
                    Your cart total (${total.toFixed(2)}) meets the ${deliveryInfo.minOrder} delivery minimum.
                  </span>
                ) : (
                  <span>
                    Your cart is <span className="text-[var(--mm-accent)]">${total.toFixed(2)}</span>. Add <span className="text-[var(--mm-accent)]">${(deliveryInfo.minOrder - total).toFixed(2)}</span> more to reach the ${deliveryInfo.minOrder} delivery minimum.
                  </span>
                )}
              </div>
            )}

            <form onSubmit={submitEnquiry} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </div>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Delivery address"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="Postcode"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
                <div className="flex items-center justify-between border border-[var(--mm-line)] px-4 py-3 text-sm">
                  <span className="text-[var(--mm-cream-muted)]">Estimated fee</span>
                  <span className="font-display text-xl text-[var(--mm-accent)]">
                    {zone ? `$${zone.fee}` : "—"}
                  </span>
                </div>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Order details (dishes, quantities, delivery time, special requests)"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none resize-none"
              />
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? "Sending..." : "Request Delivery"}
              </button>
              <p className="text-xs text-[var(--mm-cream-muted)] text-center font-light">
                Or call us directly on{" "}
                <a href={`tel:${restaurantInfo.contact.phone}`} className="text-[var(--mm-accent)] link-underline">
                  {restaurantInfo.contact.phoneDisplay}
                </a>
              </p>
            </form>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto text-center p-10 border border-[var(--mm-accent)] fade-in">
            <CheckCircle2 size={40} className="mx-auto text-[var(--mm-accent)] mb-4" />
            <h3 className="font-display italic text-3xl text-[var(--mm-cream)] mb-2">Enquiry received</h3>
            <p className="text-[var(--mm-cream-muted)] font-light">
              Thanks {name.split(" ")[0]} — mama will call you shortly on {phone} to confirm the order and delivery time.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
